/**
 * API base resolution. The embed talks to the host that served it: we capture
 * `document.currentScript` synchronously at load (it is only set while the classic script runs)
 * and derive `{origin}{dir}` from its `src`. A `data-ffs-base` attribute on the mount element
 * overrides it (useful for test pages that load a local build against a different API host).
 */

const currentScript =
  typeof document !== 'undefined' ? (document.currentScript as HTMLScriptElement | null) : null;

/** `https://sessions.fullfocus.co/embed.js` → `https://sessions.fullfocus.co`. */
export function baseFromScriptSrc(src: string | null | undefined, href?: string): string | null {
  if (!src) return null;
  try {
    const u = new URL(src, href ?? (typeof location !== 'undefined' ? location.href : undefined));
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.origin + u.pathname.replace(/\/[^/]*$/, '');
  } catch {
    return null;
  }
}

/** Resolved once at load; null when the script was inlined or `currentScript` is unavailable. */
export const scriptBase: string | null = baseFromScriptSrc(currentScript?.src);

export function resolveBase(host: Element, fallback: string | null = scriptBase): string {
  const attr = host.getAttribute('data-ffs-base')?.trim();
  if (attr) return attr.replace(/\/+$/, '');
  return fallback ?? '';
}
